import { AfterViewInit, ChangeDetectionStrategy, Component, computed, Signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { combineLatest, firstValueFrom, interval, Observable } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

import { RootState } from '@root/app.state';
import { AuthorizeService } from '@platform/auth/authorize.service';
import * as PlatformActions from '@platform/platform.actions';
import { selectUser } from '@platform/platform.selectors';
import * as CoreActions from '@core/core.actions';
import { selectAllCustomerSpaces } from '@core/core.selectors';
import { CustomerSpace } from '@core/core.state';
import { filterSucceededResource } from '@platform/shared/resource.state';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { IOptionsStoreWithFilter, OptionsStoreService, UiTableKeys } from '@platform/components/exper-table/sort-options-store.service';

type CustomerSpaceSelectType = 'MINE' | 'ACTIVE' | 'INACTIVE';

@Component({
  selector: 'app-customer-space-list',
  templateUrl: './customer-space-list.component.html',
  styleUrls: ['./customer-space-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerSpaceListComponent implements AfterViewInit {
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  public readonly dataSource = new MatTableDataSource<CustomerSpace>([]);

  public readonly filterForm = new FormGroup({
    search: new FormControl<string | null>(''),
    customerSpaceSelect: new FormControl<CustomerSpaceSelectType>('MINE'),
  });

  public readonly customerSpaceSelectionTypeTranslationKeyMapping: Record<CustomerSpaceSelectType, string> = {
    MINE: 'customer-spaces.customer-space-list.my-customer-spaces',
    ACTIVE: 'customer-spaces.customer-space-list.active-customer-spaces',
    INACTIVE: 'customer-spaces.customer-space-list.inactive-customer-spaces',
  };
  public readonly customerSpaceSelections = computed<CustomerSpaceSelectType[]>(() =>
    this.isPortalAdmin() ? ['MINE', 'ACTIVE', 'INACTIVE'] : ['MINE', 'INACTIVE'],
  );

  public readonly allowedCustomerSpaces: Signal<CustomerSpace[] | undefined>;
  public readonly isInternalUser: Signal<boolean>;
  private readonly isPortalAdmin: Signal<boolean>;
  private readonly optionsStore: IOptionsStoreWithFilter<{ customerSpaceSelect: CustomerSpaceSelectType }>;

  constructor(
    private store$: Store<RootState>,
    private authorizeService: AuthorizeService,
    optionsStoreService: OptionsStoreService,
  ) {
    this.optionsStore = optionsStoreService.getOptionsStore<{ customerSpaceSelect: CustomerSpaceSelectType }>(
      UiTableKeys.CustomerSpaceList,
      { customerSpaceSelect: 'MINE' },
    );

    const customerSpaces$ = store$.pipe(select(selectAllCustomerSpaces), filterSucceededResource());
    const allowedCustomerSpaces$ = customerSpaces$.pipe(switchMap((customerSpaces) => this.getAllowedCustomerSpaces(customerSpaces)));

    this.allowedCustomerSpaces = toSignal(allowedCustomerSpaces$);

    this.isPortalAdmin = toSignal(authorizeService.isAuthorized('PORTAL_ADMIN', null!), { initialValue: false });
    this.isInternalUser = toSignal(
      store$.pipe(
        select(selectUser),
        filterSucceededResource(),
        map((user) => user.isInternal),
      ),
      { initialValue: false },
    );

    combineLatest([
      customerSpaces$,
      allowedCustomerSpaces$,
      this.filterForm.controls.customerSpaceSelect.valueChanges.pipe(startWith('MINE' as CustomerSpaceSelectType)),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([customerSpaces, allowedCustomerSpaces, customerSpaceSelect]) => {
        this.dataSource.data = customerSpaces.filter((customerSpace) =>
          isCustomerSpaceSelected(customerSpace, customerSpaceSelect, allowedCustomerSpaces),
        );
      });

    this.filterForm.controls.search.valueChanges.pipe(takeUntilDestroyed()).subscribe((searchValue) => {
      this.dataSource.filter = searchValue ?? '';
    });

    this.dataSource.filterPredicate = (item, filter) => item.name.toLowerCase().includes(filter.toLowerCase());
    this.dataSource.sortData = (items, sort) => [...items].sort((a, b) => compareCustomerSpaces(a, b, sort));

    interval(1000 * 30)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.store$.dispatch(PlatformActions.loadCurrentUser());
        this.store$.dispatch(CoreActions.loadCustomerSpaces());
      });
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;

    this.optionsStore.registerSort(this.sort);
    this.optionsStore.registerPaginatorPageSize(this.paginator);
    this.optionsStore.registerFilter('customerSpaceSelect', this.filterForm.controls.customerSpaceSelect);
  }

  public isAuthorized(customerSpace: CustomerSpace): Observable<boolean> {
    const values = new Map<string, string>();
    values.set('customerSpaceId', customerSpace.id);
    return this.authorizeService.isAuthorized('COCKPIT_READ', values);
  }

  public toggleFavorite(customerSpace: CustomerSpace): void {
    if (customerSpace.isFavorite) {
      this.store$.dispatch(CoreActions.deleteCustomerSpaceFavorite({ customerSpaceId: customerSpace.id }));
    } else {
      this.store$.dispatch(CoreActions.addCustomerSpaceFavorite({ customerSpaceId: customerSpace.id }));
    }
  }

  public isMyCustomerSpace(customerSpace: CustomerSpace): boolean {
    return isMyCustomerSpace(customerSpace, this.allowedCustomerSpaces());
  }

  private async getAllowedCustomerSpaces(customerSpaces: CustomerSpace[]): Promise<CustomerSpace[]> {
    const result = [];
    for (const customerSpace of customerSpaces) {
      if (await firstValueFrom(this.isAuthorized(customerSpace))) {
        result.push(customerSpace);
      }
    }
    return result;
  }
}

const isMyCustomerSpace = (customerSpace: CustomerSpace, allowedCustomerSpaces: CustomerSpace[] | undefined): boolean =>
  allowedCustomerSpaces?.find((x) => x.id === customerSpace.id) !== undefined;

const isCustomerSpaceSelected = (
  customerSpace: CustomerSpace,
  customerSpacesSelection: CustomerSpaceSelectType | null,
  allowedCustomerSpaces: CustomerSpace[] | undefined,
): boolean => {
  switch (customerSpacesSelection) {
    case 'ACTIVE':
      return customerSpace.isActive;
    case 'INACTIVE':
      return !customerSpace.isActive;
    case 'MINE':
    case null:
      return customerSpace.isActive && isMyCustomerSpace(customerSpace, allowedCustomerSpaces);
  }
};

const compareCustomerSpaces = (a: CustomerSpace, b: CustomerSpace, sort: MatSort) => {
  if (a.isFavorite !== b.isFavorite) {
    return a.isFavorite ? -1 : 1;
  }

  const compareResult = getSortPropertyValue(a, sort).localeCompare(getSortPropertyValue(b, sort));
  return sort.direction === 'desc' ? -compareResult : compareResult;
};

const getSortPropertyValue = (customerSpace: CustomerSpace, sort: MatSort): string => {
  switch (sort.active) {
    case 'name':
      return customerSpace.name;
    case 'tasks':
      return `${customerSpace.openTaskCount ?? 0}`;
    case 'notifications':
      return `${customerSpace.notificationCount ?? 0}`;
    default:
      return '';
  }
};
